/**
 * DataBest — Quick voice report button for the Today page.
 * Idle → listening (red, pulsing, live transcript) → processing → result.
 */
import React, { useState } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import useSpeechRecognition from "@/features/voice/hooks/useSpeechRecognition";
import { voiceApi } from "@/features/voice/api/voiceApi";

const VoiceReportButton = ({ individualId, onResult }) => {
    const [processing, setProcessing] = useState(false);

    const handleFinal = async (transcript) => {
        const text = (transcript || "").trim();
        if (!text) {
            toast.info("No se detectó voz — intenta de nuevo");
            return;
        }
        if (!individualId) return;
        setProcessing(true);
        try {
            const data = await voiceApi.report(individualId, text);
            onResult?.(data);
            const s = data.summary || {};
            toast.success(
                `${s.tasks_saved || 0} guardadas · ${s.needs_review || 0} por revisar · ${s.incidents || 0} incidentes`,
            );
        } catch (err) {
            toast.error(err?.data?.detail || err?.message || "No se pudo procesar el reporte por voz");
        } finally {
            setProcessing(false);
        }
    };

    const { supported, listening, interim, start, stop } = useSpeechRecognition({
        onFinal: handleFinal,
    });

    const disabled = !individualId || processing;

    const handleClick = () => {
        if (!supported) {
            toast.error("El dictado por voz requiere Chrome, Edge o Safari.");
            return;
        }
        if (listening) stop();
        else start();
    };

    const label = !supported
        ? "No soportado"
        : processing
            ? "Procesando…"
            : listening
                ? "Escuchando… toca para detener"
                : "Reporte rápido por voz";

    return (
        <div className="flex flex-col items-center gap-2">
            <button
                type="button"
                onClick={handleClick}
                disabled={disabled && !listening}
                data-testid="voice-report-button"
                title={!individualId ? "Selecciona un individuo" : label}
                className={`relative flex h-14 w-14 items-center justify-center rounded-full transition-all ${
                    listening
                        ? "scale-110 bg-red-600 shadow-lg shadow-red-300"
                        : "bg-brand-blue-700 hover:bg-brand-blue-800"
                } text-white disabled:opacity-40`}
                aria-label={label}
            >
                {processing ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                ) : !supported ? (
                    <MicOff className="h-6 w-6" />
                ) : (
                    <Mic className={`h-6 w-6 ${listening ? "animate-pulse" : ""}`} />
                )}
                {listening && (
                    <span
                        data-testid="voice-listening-indicator"
                        className="absolute inset-0 rounded-full ring-4 ring-red-400/50 animate-ping"
                    />
                )}
            </button>
            <span className="max-w-[120px] text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {label}
            </span>
            {listening && interim && (
                <p
                    data-testid="voice-transcript-preview"
                    className="max-w-[220px] truncate rounded-md bg-slate-100 px-2 py-1 text-[11px] text-slate-600"
                >
                    {interim}
                </p>
            )}
        </div>
    );
};

export default VoiceReportButton;
